import { Todo as TodoNode } from "@acto/gql/graphql";
import TodoStatus from "./TodoStatus";
import User from "./User";

export interface TodoClient {
  id: string;
  name: string;
  externalReference?: string;
  legacyId?: string;
}

//TODO: check if this logic is duplicated elsewhere
// The backend serialises `dueDate` as a date-only `YYYY-MM-DD` string (Kotlin `LocalDate.toString()`).
// `new Date("YYYY-MM-DD")` parses as UTC midnight per the ECMAScript spec, so day-level comparisons
// and labels skew by the viewer's UTC offset (e.g. the de-DE audience at UTC+1/+2 saw "0d overdue"
// for an item due yesterday). Parse to LOCAL midnight so the Date represents the intended calendar day.
function parseDueDate(value: string): Date {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) {
    return new Date(value);
  }
  const [, year, month, day] = match;
  return new Date(Number(year), Number(month) - 1, Number(day));
}

export default class Todo {
  constructor(
    public readonly id: string,
    public readonly title: string,
    public readonly status: TodoStatus,
    public readonly description?: string,
    public readonly dueDate?: Date,
    public readonly processedReason?: string,
    public readonly isWorkflow?: boolean,
    public readonly isNoteBased?: boolean,
    public readonly client?: TodoClient,
    // The user the todo is assigned to. Nullable for every todo kind: a manual todo may be created
    // unassigned, and not every query selects `assignedUser` (those callers get `undefined` here).
    public readonly assignee?: User,
    /** Creation date as the backend's `YYYY-MM-DD` string; undefined when the query didn't select it. */
    public readonly createdAt?: string,
  ) {}

  static fromGraphql(data: TodoNode): Todo {
    if (data instanceof Todo) {
      return data;
    }

    const parsedDate = data.dueDate ? parseDueDate(data.dueDate) : undefined;
    const isValidDate = parsedDate && !isNaN(parsedDate.getTime());

    return new Todo(
      data.id,
      data.title ?? "",
      TodoStatus.fromGraphql(data.processedStatus),
      data.description ?? undefined,
      isValidDate ? parsedDate : undefined,
      data.processedReason ?? undefined,
      data.isWorkflow ?? undefined,
      data.isNoteBased ?? undefined,
      data.client
        ? {
            id: data.client.id,
            name: data.client.name,
            externalReference: data.client.externalReference ?? undefined,
            legacyId: data.client.legacyId ?? undefined,
          }
        : undefined,
      data.assignedUser ? User.fromGraphql(data.assignedUser) : undefined,
      data.createdAt ?? undefined,
    );
  }

  /**
   * Returns `this` when the todo already holds [assignee], otherwise a copy with [assignee] set.
   * Todos are immutable, so an optimistic assignee (picked locally, not yet confirmed by the
   * server) renders through a copy, never through mutation.
   */
  public withAssignee(assignee?: User): Todo {
    if (assignee === this.assignee) return this;

    // Copied field-by-field rather than rebuilt through the constructor: a positional call would
    // silently drop any field added to this class later, and the loss would only show while an
    // optimistic assignee is applied.
    const override: Pick<Todo, "assignee"> = { assignee };
    return Object.assign(Object.create(Todo.prototype) as Todo, this, override);
  }

  public isOpen(): boolean {
    return this.status.isOpen();
  }

  public isCompleted(): boolean {
    return this.status.isCompleted();
  }
}
